/**
 * C1–C5's navigation and the child switcher, as one pure machine.
 *
 * Pure for the reason `teacherFlow.ts` and `kidFlow.ts` are, and one sharper:
 * **this repo has no DOM test renderer** (OD-60), so a switcher living in
 * `useState` inside a component is a switcher nothing can test. The transitions
 * that matter here are claims about a graph, not about a button.
 *
 * 🔴 THE CLAIM THIS FILE HAS TO KEEP: **the active child is always one of the
 * parent's own.** Every per-child request downstream takes `activeChildId` from
 * here and puts it in a path, so a state where it names a child the server did
 * not return is a state that issues requests for somebody else's child. The
 * server would refuse them — `OwnPlayerGuard` and the service both check — but a
 * client that can *form* the request is a client one bad guard away from a
 * breach, and it would show the parent a 403 they cannot explain.
 */

export type ParentScreen =
  /** C1 — the 30-second read. */
  | "C1"
  /** C2 — progress detail. */
  | "C2"
  /** C3 — achievements. */
  | "C3"
  /** C4 — notifications. */
  | "C4"
  /**
   * The school's notice board.
   *
   * 🔴 ACCOUNT-WIDE, LIKE C4 AND C5, AND THAT IS WHY IT IS IN THE SAME EXEMPTION
   * BELOW. An announcement is a fact about a school and every family gets the
   * same one — there is no `playerId` in the read and none in the model — so a
   * parent whose children are not linked to their number yet can still be told
   * the school is closed on Friday. Gating it on an active child would hide the
   * one screen that works for exactly the parent whose account is still being
   * set up.
   */
  | "ANNOUNCEMENTS"
  /** C6 — attendance, which is per-child like C1–C3 rather than account-wide. */
  | "C6"
  /**
   * The conversation with this child's teacher.
   *
   * ✅ C2's "Message the teacher" BUTTON HAS BEEN DISABLED SINCE THIS TIER
   * SHIPPED, with the honest reason attached — §9.2 owned the only send path and
   * that path is a one-way WhatsApp template. This is the screen it opens now.
   *
   * 🔴 PER CHILD, LIKE C1–C3 AND C6, AND NOT ACCOUNT-WIDE. The thread is keyed
   * on the player: a parent of two has two conversations, with two teachers,
   * about two children. Folding them into one inbox would be a conversation
   * where the wrong child's teacher is in the room.
   */
  | "THREAD"
  /**
   * 🔴 THE CONSENT SCREEN — contract D1, and it is a SCREEN rather than a step
   * in the login. *"Signing in is not consenting."* It is offered from C1 when
   * the state is PENDING, and it is never entered automatically by verifying an
   * OTP.
   *
   * ✅ AND IT IS THIS TIER'S SETTINGS PAGE TOO (ADR-051). C5 was removed — a
   * parent had one word pointing at two screens — and its controls, including
   * the per-child recording switch I5 makes load-bearing, are drawn here.
   */
  | "CONSENT";

export interface ParentFlowState {
  screen: ParentScreen;
  /** The switcher's options, as the server returned them. */
  childIds: readonly string[];
  /** Null only when the parent has no children at all. */
  activeChildId: string | null;
}

export type ParentAction =
  | { type: "CHILDREN_LOADED"; childIds: readonly string[] }
  | { type: "SWITCH_CHILD"; playerId: string }
  | { type: "OPEN"; screen: ParentScreen }
  | { type: "BACK" };

/**
 * The screens that do not need a child.
 *
 * 🔴 A LIST, NOT A CHAIN OF `!==`. It was two inline comparisons and the third
 * screen was the one that would have been forgotten — a parent with nothing
 * linked would have been bounced off the announcements board back to a dashboard
 * that has nothing to show them. Named here, the next account-wide screen is one
 * entry rather than one more clause in a condition nobody re-reads.
 */
export const ACCOUNT_WIDE: readonly ParentScreen[] = [
  "C4",
  "ANNOUNCEMENTS",
  /*
   * ✅ CONSENT JOINED THIS LIST WITH C5'S CONTENTS (ADR-051), AND LEAVING IT OFF
   * WOULD HAVE BEEN A SILENT REGRESSION. C5 was account-wide because its top
   * half — the messaging language, what we may send, when not to send it — is
   * about the ACCOUNT, and a parent whose children are not linked yet still has
   * all three. Folding it into a per-child screen without this line would have
   * taken those settings away from exactly the family still being set up.
   *
   * ⚠️ THE CONSENT HALF IS STILL GUARDED ON A CHILD, in the render rather than
   * here: with no child the page draws the preferences alone rather than
   * `/parent/children/undefined/consent`.
   */
  "CONSENT",
];

export const initialParentFlow = (): ParentFlowState => ({
  screen: "C1",
  childIds: [],
  activeChildId: null,
});

export function parentFlow(
  state: ParentFlowState,
  action: ParentAction,
): ParentFlowState {
  switch (action.type) {
    case "CHILDREN_LOADED": {
      const childIds = [...action.childIds];
      /*
       * 🔴 KEEP THE ACTIVE CHILD IF THEY ARE STILL IN THE LIST. A refetch —
       * a retry, a tab regaining focus — must not silently move the parent of
       * two children back to the first one while they are reading the second
       * one's progress.
       */
      const keep =
        state.activeChildId !== null && childIds.includes(state.activeChildId);
      return {
        ...state,
        childIds,
        /*
         * ⚠️ AND IF THEY ARE NOT, FALL BACK TO THE FIRST RATHER THAN HOLDING A
         * STALE ID. A child removed from the roster between loads would
         * otherwise leave every request on this screen pointing at a player the
         * parent no longer has.
         */
        activeChildId: keep ? state.activeChildId : (childIds[0] ?? null),
      };
    }

    case "SWITCH_CHILD":
      /*
       * 🔴 A NO-OP FOR AN ID THAT IS NOT IN THE LIST. The switcher is rendered
       * from `childIds`, so this cannot happen from a tap — but it is the one
       * transition that could put another parent's child in a request path, and
       * "cannot happen from the UI" is not a property of a reducer.
       */
      if (!state.childIds.includes(action.playerId)) return state;
      return { ...state, activeChildId: action.playerId };

    case "OPEN":
      /*
       * ⚠️ EVERY SCREEN EXCEPT THE THREE ACCOUNT-WIDE ONES IS ABOUT ONE CHILD, so
       * opening one without an active child would render a per-child screen with
       * no id and issue a request for `undefined`. C4 (the feed), C5's account
       * half and the announcements board are about the ACCOUNT, so they stay
       * reachable for a parent with no children — which is also the only way such
       * a parent can turn messaging off, and the only way they can be told the
       * school is shut while the roster is still being linked.
       */
      if (
        state.activeChildId === null &&
        !ACCOUNT_WIDE.includes(action.screen)
      ) {
        return state;
      }
      return { ...state, screen: action.screen };

    case "BACK":
      /*
       * Everything returns to C1. The parent tier is a hub and four leaves, not
       * a stack — a back button that unwound a history would let a parent walk
       * backwards into the consent screen they just completed.
       */
      return state.screen === "C1" ? state : { ...state, screen: "C1" };

    default:
      return state;
  }
}

/**
 * Whether the switcher should be drawn at all.
 *
 * P8 puts multiple children in C1's switcher; a parent with one child does not
 * need a control that has one option, and on a phone that control costs a row
 * of the 30-second read.
 */
export const showsChildSwitcher = (state: ParentFlowState): boolean =>
  state.childIds.length > 1;

/**
 * The line C1 shows above the child's data.
 *
 * 🔴 IT NAMES THE CHILD, for the same reason D3's confirm line does: two
 * children called Aarav is the ordinary case in an Indian family as well as an
 * Indian classroom, and a parent switching between siblings on a phone needs
 * the screen to say which one they are looking at.
 */
export function activeChildLine(
  child: { name: string; className: string | null } | null,
): string {
  if (!child) return "No children linked yet";
  /*
   * ⚠️ Deliberately not a copy-table string: a child's name and their class
   * name are both proper nouns the school supplied, and the separator is the
   * same middot D1's welcome line uses. A template around two untranslatable
   * values would be a string to maintain that changes nothing on screen.
   */
  return child.className ? `${child.name} · ${child.className}` : child.name;
}
